import { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShieldAlert, IdCard } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchReports, type AdminReport } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A6.01 — Umumiy moderatsiya navbati. GET /admin/reports (backend `4f05814`).
 * Shikoyatlar userlardan keladi (POST /reports — D-yo'li «Пожаловаться» tugmasini
 * ulaydi). Kategoriya/holat ustun-filtri bilan (A6.03–A6.08 navbatlari).
 */
export function ModerationQueue() {
  const t = useT()
  const navigate = useNavigate()

  const rowActions = useMemo<RowAction<AdminReport>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/moderation/${r.id}`),
      },
    ],
    [t, navigate],
  )

  const columns = useMemo<AdminColumn<AdminReport>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      { key: 'created_at', header: t.col.time, type: 'date', width: 150 },
      {
        key: 'category',
        header: t.mod.category,
        type: 'enum',
        width: 130,
        options: [
          { value: 'csam', label: t.mod.catCsam, status: 'banned' },
          { value: 'violence', label: t.mod.catViolence, status: 'banned' },
          { value: 'fraud', label: t.mod.catFraud, status: 'review' },
          { value: 'spam', label: t.mod.catSpam, status: 'review' },
          { value: 'copyright', label: t.mod.catCopyright, status: 'review' },
          { value: 'doxxing', label: t.mod.catDoxxing, status: 'banned' },
          { value: 'other', label: t.mod.catOther, status: 'deleted' },
        ],
      },
      {
        key: 'target_type',
        header: t.mod.target,
        type: 'text',
        width: 150,
        sortable: false,
        render: (r) => (
          <span className="font-mono text-xs">
            {r.target_type}#{r.target_id}
          </span>
        ),
      },
      {
        key: 'reporter_id',
        header: t.mod.reporter,
        type: 'text',
        width: 140,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.reporter_id}`} className="text-accent hover:underline">
            {r.reporter_username ? `@${r.reporter_username}` : `#${r.reporter_id}`}
          </Link>
        ),
      },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 130,
        options: [
          { value: 'pending', label: t.mod.pending, status: 'pending' },
          { value: 'resolved', label: t.mod.resolved, status: 'active' },
          { value: 'dismissed', label: t.mod.dismissed, status: 'deleted' },
        ],
      },
      { key: 'reason', header: t.mod.reason, type: 'longtext', minWidth: 180, hiddenByDefault: true },
    ],
    [t],
  )

  const fetcher = useCallback(fetchReports, [])

  const cats: { key: string; label: string }[] = [
    { key: 'csam', label: t.mod.catCsam },
    { key: 'violence', label: t.mod.catViolence },
    { key: 'spam', label: t.mod.catSpam },
    { key: 'fraud', label: t.mod.catFraud },
    { key: 'copyright', label: t.mod.catCopyright },
    { key: 'doxxing', label: t.mod.catDoxxing },
  ]

  return (
    <>
      <PageHeader
        title={t.mod.queue}
        screenId="A6.01"
        description={t.mod.queueNote}
        actions={
          <div className="flex flex-wrap items-center gap-1">
            <Link
              to="/moderation/groups"
              className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-xs text-secondary hover:border-border-strong hover:text-primary"
            >
              {t.mod.groups}
            </Link>
            {cats.map((c) => (
              <Link
                key={c.key}
                to={`/moderation/cat/${c.key}`}
                className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-xs text-secondary hover:border-border-strong hover:text-primary"
              >
                {c.label}
              </Link>
            ))}
          </div>
        }
      />
      <DataTable<AdminReport>
        screenId="A6.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <ShieldAlert size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
